import * as React from 'react';
import b_ from 'b_';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { change } from 'redux-form';
import { connect } from 'react-redux';
import translate, { InjectedTranslateProps } from 'shared/utils/translate';
import { validate } from 'shared/utils/validators';
import SubmitButton from 'shared/components/SubmitButton';
import { form, FormProps } from 'shared/utils/form';
import { FormData, constraints } from './constraints';
import { routine } from './actions';
import { selector, Selector } from './selectors';
import { Idea } from '../../../../types';
import BuyAndHoldSubmitButton from '../BuyAndHoldSubmitButton';
import BetterOfferHint from '../BetterOfferHint/index';
import { AmountInput } from 'shared/components/AmountInput';
import { Money } from 'shared/types/common';
import { Investment_IdeaType } from '../../../../../../../__generated__/globalTypes';

import './index.scss';

export interface Props {
  idea: Idea;
  min: Money;
  max: Money;
  initialAmount: Money;
  balance: Money;
}

type Enhanced = Props &
  InjectedTranslateProps &
  Selector &
  ReturnType<typeof mapDispathcToProps> &
  FormProps<FormData, Props>;

const formName = 'idea-landing-submit';

const b = b_.with('idea-landing-submit');

const mapDispathcToProps = (dispatch: Dispatch) => ({
  setAmount: (value: number) => dispatch(change(formName, 'amount', value)),
});

const enhance = compose<Enhanced, Props>(
  translate,
  connect(selector, mapDispathcToProps),
  form<FormData, Props>(routine, {
    form: formName,
    enableReinitialize: true,
    validate: (values, { min, max }) => validate(constraints(min.value, max.value), values),
  })
);

class Submit extends React.Component<Enhanced> {
  render() {
    const { balance, min, max, idea, formSubmit, amount, setAmount } = this.props;

    const nextLimitIndex = this.getBetterOfferHint();

    return (
      <form className={b()} onSubmit={formSubmit} data-testid="order-submit-form">
        <AmountInput
          name="amount"
          classNameInput={b('input')}
          currency={idea.enterAmount.currency}
          min={min.value}
          max={max.value}
          tooltipPlacement="bottom"
        />
        <div>{this.renderButton()}</div>
        {idea.tariffGrid && nextLimitIndex && (
          <div className={b('tariff-hint-wrapper')}>
            <BetterOfferHint
              tariffGrid={idea.tariffGrid}
              amount={amount}
              nextLimitIndex={nextLimitIndex}
              balance={balance.value}
              setAmount={setAmount}
            />
          </div>
        )}
        <div>{this.renderComment()}</div>
      </form>
    );
  }

  private renderButton() {
    const { t, idea, submitting, error } = this.props;

    if (idea.currentPrice) {
      return (
        <BuyAndHoldSubmitButton
          currentPrice={idea.currentPrice}
          submitting={submitting}
          error={error}
        />
      );
    }

    return (
      <SubmitButton
        className={b('submit-btn')}
        submitting={submitting}
        error={error}
        data-testid="submit-btn"
      >
        {t('Invest')}
      </SubmitButton>
    );
  }

  private renderComment() {
    const { t, idea } = this.props;

    if (idea.ideaType === Investment_IdeaType.BUY_AND_HOLD) {
      return null;
    }

    if (idea.currentPrice) {
      return <p className={b('comment')}>{t('Actual entry price may slightly differ')}</p>;
    }

    return null;
  }

  private getBetterOfferHint(): number | null {
    const {
      amount,
      idea: { tariffGrid },
    } = this.props;

    if (!tariffGrid || !amount || isNaN(amount)) {
      return null;
    }

    const { tariffs } = tariffGrid;

    const upLimitIndex = tariffs.findIndex((tariff) => tariff.requestAmount > amount);

    if (upLimitIndex < 1) {
      return null;
    }

    const actualTariff = tariffs[upLimitIndex - 1];
    const nextTariff = tariffs[upLimitIndex];
    const actualFeeValue = amount * (actualTariff.enterFee + actualTariff.leaveFee);
    const nextFeeValue = nextTariff.requestAmount * (nextTariff.enterFee + nextTariff.leaveFee);

    if (actualFeeValue > nextFeeValue) {
      return upLimitIndex;
    }
    return null;
  }
}

export default enhance(Submit);
